/**
 * Store de gestion de la vue (extérieur/intérieur)
 * Gère l'état de la vue actuellement affichée pour synchroniser l'interface
 */

import { defineStore } from 'pinia';

/**
 * Type de vue disponible
 */
export type ViewType = 'exterior' | 'interior';

/**
 * État du store de vue
 */
interface ViewState {
  /** Vue actuellement active */
  currentView: ViewType;
}

export const useViewStore = defineStore('view', {
  state: (): ViewState => ({
    currentView: 'exterior',
  }),

  getters: {
    /**
     * Retourne true si la vue extérieure est active
     */
    isExteriorView: (state): boolean => state.currentView === 'exterior',

    /**
     * Retourne true si la vue intérieure est active
     */
    isInteriorView: (state): boolean => state.currentView === 'interior',

    /**
     * Retourne le type de vue actuel
     */
    viewType: (state): ViewType => state.currentView,
  },

  actions: {
    /**
     * Définit la vue actuelle
     * @param view Type de vue à activer ('exterior' ou 'interior')
     */
    setView(view: ViewType): void {
      this.currentView = view;
    },

    /**
     * Bascule vers la vue extérieure
     */
    setExteriorView(): void {
      this.currentView = 'exterior';
    },

    /**
     * Bascule vers la vue intérieure
     */
    setInteriorView(): void {
      this.currentView = 'interior';
    },

    /**
     * Émet un événement personnalisé pour synchroniser avec les composants legacy
     */
    emitViewChange(): void {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('viewModeChange', {
          detail: { mode: this.currentView }
        }));
      }
    },

    /**
     * Définit la vue et émet l'événement de changement
     * @param view Type de vue à activer
     */
    setViewWithEmit(view: ViewType): void {
      this.currentView = view;
      this.emitViewChange();
    },
  },
});
